import { useRef, useState, useCallback, useEffect } from 'react'
import { motion } from 'framer-motion'
import gsap from 'gsap'

type DeviceOrientationPermissionState = 'granted' | 'denied' | 'prompt'

type DeviceOrientationWithPermission = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<DeviceOrientationPermissionState>
}

const MOBILE_MEDIA_QUERY = '(max-width: 768px)'
const TOUCH_EVENT_NAME = 'ontouchstart'

interface TiltCardProps {
  children?: React.ReactNode
  className?: string
}

/**
 * Card com efeitos premium
 * - Desktop: Tilt responsivo ao mouse
 * - Mobile: Animação automática de luz + gyroscope
 */
export function TiltCard({ children, className = '' }: TiltCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 })
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches || TOUCH_EVENT_NAME in window
  })
  const [gyroSupported, setGyroSupported] = useState(false)

  // Detect mobile & setup gyroscope
  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
    const handleMediaChange = () => {
      setIsMobile(mediaQuery.matches || TOUCH_EVENT_NAME in window)
    }

    if (mediaQuery.matches && typeof DeviceOrientationEvent !== 'undefined') {
      // Request permission on iOS 13+
      const requestPermission = async () => {
        const deviceOrientationEvent = DeviceOrientationEvent as DeviceOrientationWithPermission

        if (typeof deviceOrientationEvent.requestPermission === 'function') {
          try {
            const permission = await deviceOrientationEvent.requestPermission()
            if (permission === 'granted') {
              setGyroSupported(true)
            }
          } catch {
            // Permission denied or not available
          }
        } else {
          // Android or older iOS - permission not needed
          setGyroSupported(true)
        }
      }
      requestPermission()
    }

    mediaQuery.addEventListener('change', handleMediaChange)
    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange)
    }
  }, [])

  // Gyroscope handler for mobile
  useEffect(() => {
    if (!isMobile || !gyroSupported || !cardRef.current) return

    const handleOrientation = (e: DeviceOrientationEvent) => {
      if (e.beta === null || e.gamma === null) return

      // Clamp values and make them subtle
      const tiltX = Math.max(-15, Math.min(15, e.beta - 45)) * 0.3
      const tiltY = Math.max(-15, Math.min(15, e.gamma)) * 0.3

      gsap.to(cardRef.current, {
        rotateX: -tiltX,
        rotateY: tiltY,
        duration: 0.3,
        ease: 'power2.out',
      })
    }

    window.addEventListener('deviceorientation', handleOrientation)
    return () => window.removeEventListener('deviceorientation', handleOrientation)
  }, [isMobile, gyroSupported])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!cardRef.current || isMobile) return

      const rect = cardRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const centerX = rect.width / 2
      const centerY = rect.height / 2

      const normalX = (x / rect.width) * 100
      const normalY = (y / rect.height) * 100
      setMousePos({ x: normalX, y: normalY })

      const rotateX = ((y - centerY) / centerY) * -6
      const rotateY = ((x - centerX) / centerX) * 6

      gsap.to(cardRef.current, {
        rotateX,
        rotateY,
        duration: 0.4,
        ease: 'power2.out',
      })
    },
    [isMobile]
  )

  const handleMouseEnter = useCallback(() => {
    if (isMobile) return
    setIsHovering(true)
    if (cardRef.current) {
      gsap.to(cardRef.current, {
        scale: 1.02,
        duration: 0.3,
        ease: 'power2.out',
      })
    }
  }, [isMobile])

  const handleMouseLeave = useCallback(() => {
    if (isMobile) return
    setIsHovering(false)
    setMousePos({ x: 50, y: 50 })
    if (cardRef.current) {
      gsap.to(cardRef.current, {
        rotateX: 0,
        rotateY: 0,
        scale: 1,
        duration: 0.5,
        ease: 'power2.out',
      })
    }
  }, [isMobile])

  return (
    <div className="perspective-1000" style={{ perspective: '1000px' }}>
      <motion.div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`relative transform-gpu ${className}`}
        style={{ transformStyle: 'preserve-3d' }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
      >
        {children}

        {/* Shimmer animation - sempre ativo, não precisa de interação */}
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden"
          style={{ opacity: 0.6 }}
        >
          <div
            className="absolute inset-0 shimmer-sweep"
            style={{
              background: `linear-gradient(
                105deg,
                transparent 0%,
                transparent 40%,
                rgba(255,255,255,0.3) 45%,
                rgba(255,255,255,0.5) 50%,
                rgba(255,255,255,0.3) 55%,
                transparent 60%,
                transparent 100%
              )`,
            }}
          />
        </div>

        {/* Top edge highlight - sempre visível */}
        <div
          className="absolute inset-x-0 top-0 h-[1px] rounded-t-2xl pointer-events-none"
          style={{
            background: `linear-gradient(90deg, 
              transparent 0%, 
              rgba(255,255,255,0.3) 20%,
              rgba(255,255,255,0.5) 50%,
              rgba(255,255,255,0.3) 80%,
              transparent 100%
            )`,
          }}
        />

        {/* Desktop-only: mouse-following glare */}
        {!isMobile && (
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none transition-opacity duration-300 overflow-hidden"
            style={{
              opacity: isHovering ? 1 : 0,
              background: `radial-gradient(
                ellipse 80% 50% at ${mousePos.x}% ${mousePos.y}%, 
                rgba(255,255,255,0.2) 0%, 
                rgba(255,255,255,0.08) 30%,
                transparent 70%
              )`,
            }}
          />
        )}
      </motion.div>
    </div>
  )
}
